import { MESSENGER_NOTIFICATION } from "constants/messenger"
import {
  getTokenInLocalStorage,
  removeTimestampInFileName,
} from "helpers/utils"
import { includes, isUndefined, random } from "lodash"
import { showToastProgress } from "./toastProgress"
import { DownloadQueueItem, UploadFileType } from "./downloadQueue"
import Axios, { AxiosResponse } from "axios"
import { activityLogHelper, ActivityLogType } from "helpers/activity_log"
import { encryptionGCMHelper } from "helpers/encryption-gcm"

export interface DataRequestDownloadOneFile {
  assetPath: string
  idConversationParam?: string
  project_build_id?: string
  projectId?: string
  project_component_history_id?: string
  isAdditionalInfo?: boolean
  decryptedKey?: string
}

const saveFileToDevice = (dataBlob: Blob, fileName: string) => {
  const link = document.createElement("a")
  const urlLink = URL.createObjectURL(dataBlob)
  link.href = urlLink
  link.setAttribute("download", fileName)
  document.body.appendChild(link)
  link.click()
  link?.parentNode?.removeChild(link)
  window.URL.revokeObjectURL(urlLink)
}

export const handleDownloadEncrypted = async (
  downloadId: string,
  queues: DownloadQueueItem[],
  storageKey: string,
  dataBlob: any,
  callBack: (type: "delete", downloadId) => Promise<void>,
  fileFolderName: string
) => {
  showToastProgress(
    storageKey,
    showProcessMessage(queues, "Save file to device...", downloadId),
    callBack,
    100,
    downloadId
  )
  saveFileToDevice(dataBlob, fileFolderName)
}

export const handleDownloadOneFile = async (
  dataRequest: DataRequestDownloadOneFile,
  storageKey: string,
  queues: DownloadQueueItem[],
  downloadId: string,
  callBack: (type: "delete", downloadId) => Promise<void>,
  dataBlob: Blob
) => {
  showToastProgress(
    storageKey,
    showProcessMessage(queues, "Save file to device...", downloadId),
    callBack,
    100,
    downloadId
  )
  const { assetPath } = dataRequest
  const fileName = assetPath.split("/").slice(-1)[0]
  saveFileToDevice(dataBlob, removeTimestampInFileName(fileName))
}

export const handleUploadFile = async (
  payload: any,
  item: DownloadQueueItem,
  queues: DownloadQueueItem[],
  storageKey: string,
  callBack: (type: "delete", downloadId) => Promise<void>
) => {
  if (isUndefined(item.dataUploading)) {
    return
  }
  const {
    type,
    idComponentHistory,
    file: { name },
    typeComponent,
  } = item.dataUploading

  const commonArgs: [
    typeof queues,
    typeof storageKey,
    typeof callBack,
    typeof payload,
    typeof idComponentHistory,
    typeof name
  ] = [queues, storageKey, callBack, payload, idComponentHistory, name]

  if (type === UploadFileType.GERBER) {
    return await handleUploadFileGerber(...commonArgs)
  }

  const baseTypes = [
    UploadFileType.MECHANICAL_3D,
    UploadFileType.PCB_ASSEMBLY,
    UploadFileType.PCB_STACKUP,
  ]

  if (includes(baseTypes, type)) {
    return await handleUploadFileBase(...commonArgs, type)
  }

  const bomTypes = [
    UploadFileType.MECHANICAL_BOM,
    UploadFileType.BOM,
    UploadFileType.PCB_BOM,
  ]

  if (includes(bomTypes, type)) {
    return await handleUploadFileBOM(...commonArgs, type)
  }

  if (type === UploadFileType.PCB_ATTACHMENT) {
    return await handleUploadFileAttachment(
      ...commonArgs,
      `pcb/upload-attachments`
    )
  }

  const attachmentTypes = [
    UploadFileType.MECHANICAL_ATTACHMENT,
    UploadFileType.OTHER_ATTACHMENT,
  ]

  if (includes(attachmentTypes, type)) {
    return await handleUploadFileAttachment(
      ...commonArgs,
      `${typeComponent}/upload`
    )
  }
}

const handleUploadFileGerber = async (
  queues: DownloadQueueItem[],
  storageKey: string,
  callBack: (type: "delete", downloadId) => Promise<void>,
  payload: any,
  idComponentHistory: string,
  fileName: string
) => {
  const { fileHashs, svgs, svgRequest, fileErrors, downloadId, encryptionKey } =
    payload
  showToastProgress(
    storageKey,
    showProcessMessage(queues, "Uploading file to server..", downloadId),
    callBack,
    random(15, 30),
    downloadId
  )
  if (!isUndefined(fileErrors) && fileErrors.length > 0) {
    return encryptionGCMHelper.getMessengerFileError(fileErrors)
  }
  const requestGerber = new FormData()
  const fileHash = fileHashs.length > 0 ? fileHashs[0] : null
  if (!svgRequest.length) {
    return "No SVG files to upload. Please check your input."
  }
  svgRequest.forEach((elSvg) => {
    requestGerber.append("svg", elSvg.fileName)
  })
  requestGerber.append("layers", JSON.stringify(svgs))
  if (fileHash) {
    requestGerber.append("file_hash", fileHash.file_hash)
    requestGerber.append("file_name", fileHash.file_name)
  }
  const log = await activityLogHelper.toEncryptedMessage(
    ActivityLogType.UploadPcb,
    {
      type: "gerber",
      action: "uploaded",
      fileName: fileName,
    },
    encryptionKey
  )
  requestGerber.append("log", log)

  try {
    const responseGerber = await Axios.post(
      `/api/pcb/upload-gerber/${idComponentHistory}?loading=0`,
      requestGerber
    )
    showToastProgress(
      storageKey,
      showProcessMessage(queues, "Verify signature...", downloadId),
      callBack,
      random(70, 80),
      downloadId
    )
    await encryptionGCMHelper.verifySignatureFileByGCM(
      encryptionKey,
      responseGerber.data.data.file.file_key,
      getTokenInLocalStorage()
    )

    showToastProgress(
      storageKey,
      showProcessMessage(queues, "Upload completed.", downloadId),
      callBack,
      100,
      downloadId
    )
    return "success"
  } catch (error: any) {
    return error.response?.status === 413
      ? `The upload of file larger than 50MB is not allowed`
      : error.response?.data?.message ??
          MESSENGER_NOTIFICATION.ADD_PCB_FILE_ERROR
  }
}

const handleUploadFileBOM = async (
  queues: DownloadQueueItem[],
  storageKey: string,
  callBack: (type: "delete", downloadId) => Promise<void>,
  payload: any,
  idComponentHistory: string,
  fileName: string,
  typeUpload: UploadFileType
) => {
  const { fileHashs, fileErrors, downloadId, encryptionKey } = payload
  showToastProgress(
    storageKey,
    showProcessMessage(queues, "Uploading file to server..", downloadId),
    callBack,
    random(15, 30),
    downloadId
  )
  if (!isUndefined(fileErrors) && fileErrors.length > 0) {
    return encryptionGCMHelper.getMessengerFileError(fileErrors)
  }
  const request = new FormData()
  const fileHash = fileHashs.length > 0 ? fileHashs[0] : null

  let log = ""
  let api = ""
  if (typeUpload === UploadFileType.BOM) {
    log = await activityLogHelper.toEncryptedMessage(
      ActivityLogType.UploadBom,
      {
        fileName: fileName,
      },
      encryptionKey
    )
    api = `/api/bom/upload/${idComponentHistory}`
  }
  if (typeUpload === UploadFileType.PCB_BOM) {
    log = await activityLogHelper.toEncryptedMessage(
      ActivityLogType.UploadSubBom,
      {
        type: "pcb",
        fileName: fileName,
      },
      encryptionKey
    )
    api = `/api/pcb/upload-bom/${idComponentHistory}`
  }
  if (typeUpload === UploadFileType.MECHANICAL_BOM) {
    log = await activityLogHelper.toEncryptedMessage(
      ActivityLogType.UploadSubBom,
      {
        fileName: fileName,
        type: "mechanical",
      },
      encryptionKey
    )
    api = `/api/mechanical/${idComponentHistory}/upload-bom`
  }

  if (log) {
    request.append("log", log)
  }
  if (fileHash) {
    request.append("file_hash", fileHash.file_hash)
    request.append("file_name", fileHash.file_name)
  }

  try {
    const response = await Axios.post(`${api}?loading=0`, request, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    showToastProgress(
      storageKey,
      showProcessMessage(queues, "Verify signature...", downloadId),
      callBack,
      random(70, 80),
      downloadId
    )
    await encryptionGCMHelper.verifySignatureFileByGCM(
      encryptionKey,
      response.data.data.file.file_key,
      getTokenInLocalStorage()
    )

    showToastProgress(
      storageKey,
      showProcessMessage(queues, "Upload completed.", downloadId),
      callBack,
      100,
      downloadId
    )
    return "success"
  } catch (error: any) {
    return (
      error.response?.data?.message ??
      MESSENGER_NOTIFICATION.UPLOAD_BOM_FILE_ERROR
    )
  }
}

const handleUploadFileBase = async (
  queues: DownloadQueueItem[],
  storageKey: string,
  callBack: (type: "delete", downloadId: string) => Promise<void>,
  payload: any,
  idComponentHistory: string,
  fileName: string,
  typeUpload: UploadFileType
) => {
  const { fileHashs, fileErrors, downloadId, encryptionKey } = payload
  showToastProgress(
    storageKey,
    showProcessMessage(queues, "Uploading file to server..", downloadId),
    callBack,
    random(15, 30),
    downloadId
  )
  if (!isUndefined(fileErrors) && fileErrors.length > 0) {
    return encryptionGCMHelper.getMessengerFileError(fileErrors)
  }
  const request = new FormData()
  const fileHash = fileHashs.length > 0 ? fileHashs[0] : null
  let log = ""
  let api = ""
  if (typeUpload === UploadFileType.MECHANICAL_3D) {
    log = await activityLogHelper.toEncryptedMessage(
      ActivityLogType.Upload3dFile,
      {
        fileName: fileName,
      },
      encryptionKey
    )
    api = `/api/mechanical/${idComponentHistory}/upload-3d`
  }
  if (typeUpload === UploadFileType.PCB_STACKUP) {
    log = await activityLogHelper.toEncryptedMessage(
      ActivityLogType.UploadPcb,
      {
        type: "stackup",
        action: "uploaded",
        fileName: fileName,
      },
      encryptionKey
    )
    api = `/api/pcb/upload-stackup/${idComponentHistory}`
  }
  if (typeUpload === UploadFileType.PCB_ASSEMBLY) {
    log = await activityLogHelper.toEncryptedMessage(
      ActivityLogType.UploadPcb,
      {
        type: "assembly",
        action: "uploaded",
        fileName: fileName,
      },
      encryptionKey
    )
    api = `/api/pcb/upload-assembly/${idComponentHistory}`
  }
  if (log) {
    request.append("log", log)
  }
  if (fileHash) {
    request.append("file_hash", fileHash.file_hash)
    request.append("file_name", fileHash.file_name)
  }
  try {
    const response = await Axios.post(`${api}?loading=0`, request)
    showToastProgress(
      storageKey,
      showProcessMessage(queues, "Verify signature...", downloadId),
      callBack,
      random(70, 80),
      downloadId
    )

    await encryptionGCMHelper.verifySignatureFileByGCM(
      encryptionKey,
      typeUpload === UploadFileType.MECHANICAL_3D
        ? response.data.data.file.file_key
        : response.data.data.file,
      getTokenInLocalStorage()
    )

    showToastProgress(
      storageKey,
      showProcessMessage(queues, "Upload completed.", downloadId),
      callBack,
      100,
      downloadId
    )
    return "success"
  } catch (error: any) {
    return error.response?.data?.message ?? `Upload file failed!"`
  }
}

const handleUploadFileAttachment = async (
  queues: DownloadQueueItem[],
  storageKey: string,
  callBack: (type: "delete", downloadId: string) => Promise<void>,
  payload: any,
  idComponentHistory,
  fileName: string,
  typeComponent: string
) => {
  const { fileHashs, fileErrors, downloadId, encryptionKey } = payload
  showToastProgress(
    storageKey,
    showProcessMessage(queues, "Uploading file to server..", downloadId),
    callBack,
    random(15, 30),
    downloadId
  )
  if (!isUndefined(fileErrors) && fileErrors.length > 0) {
    return encryptionGCMHelper.getMessengerFileError(fileErrors)
  }
  const request = new FormData()
  const fileHash = fileHashs.length > 0 ? fileHashs[0] : null

  const log = await activityLogHelper.toEncryptedMessage(
    ActivityLogType.UploadAttachmentFiles,
    {
      file: fileName,
    },
    encryptionKey
  )
  request.append("log", log)
  if (fileHash) {
    request.append("file_hashs", JSON.stringify([fileHash]))
  }
  try {
    const response: AxiosResponse<{
      data: {
        files: {
          file_name: string
          file_key: string
        }[]
        project_id: string
      }
    }> = await Axios.post(
      `/api/${typeComponent}/${idComponentHistory}?loading=0`,
      request
    )
    showToastProgress(
      storageKey,
      showProcessMessage(queues, "Verify signature...", downloadId),
      callBack,
      random(70, 80),
      downloadId
    )
    for (const file of response.data.data.files) {
      await encryptionGCMHelper.verifySignatureFileByGCM(
        encryptionKey,
        file.file_key,
        getTokenInLocalStorage()
      )
    }
    showToastProgress(
      storageKey,
      showProcessMessage(queues, "Upload completed.", downloadId),
      callBack,
      100,
      downloadId
    )
    return "success"
  } catch (error: any) {
    return (
      error.response?.data?.message ??
      MESSENGER_NOTIFICATION.UPLOAD_ATTACHMENT_ERROR
    )
  }
}

const showProcessMessage = (
  originQueues: DownloadQueueItem[],
  message: string,
  downloadId: string
) => {
  return originQueues.map((i) => {
    if (i.downloadId === downloadId) {
      i.processMessage = message
    }
    return i
  })
}
